import React from 'react';
import PropTypes from 'prop-types';
import {
  cx,
  defaultFormatAuthor,
  defaultFormatDate,
  getCommentAuthor,
  getCommentDate,
  getCommentText,
  isCommentOwnedByUser,
} from '../utils';
import { PaperclipIcon, EditIcon, TrashIcon } from '../Icons';
import IconBlock from '../../NewAttachment/components/IconBlock/IconBlock';
import styles from './CommentItem.module.scss';


export default function CommentItem({
  comment,
  currentUserId,
  labels,
  formatAuthor,
  formatDate,
  isCommentEditable,
  readOnly,
  isBeingEdited,
  onDownloadAttachment,
  getAttachmentUrl,
  onEdit,
  onDelete,
  deleting,
}) {
  const authorLine = (formatAuthor || defaultFormatAuthor)(
    getCommentAuthor(comment)
  );
  const dateLine = (formatDate || defaultFormatDate)(getCommentDate(comment));
  const attachments = Array.isArray(comment.attachments)
    ? comment.attachments
    : [];

  const isOwn = isCommentEditable
    ? isCommentEditable(comment, currentUserId)
    : isCommentOwnedByUser(comment, currentUserId);

  const canEdit = !readOnly && isOwn && typeof onEdit === 'function';
  const canDelete = !readOnly && isOwn && typeof onDelete === 'function';

  const handleDownloadClick = (attachment) => async (event) => {
    if (onDownloadAttachment) {
      event.preventDefault();
      onDownloadAttachment(attachment, comment);
      return;
    }
  };

  return (
    <li className={cx(styles.itemRow, isOwn && styles.itemRowOwn)}>
      {(canEdit || canDelete) && (
        <div className={styles.itemActions}>
          {canEdit && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => onEdit(comment)}
              aria-label={labels.edit}
              title={labels.edit}
              disabled={deleting}
            >
              <EditIcon />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className={cx(styles.iconButton, styles.iconButtonDanger)}
              onClick={() => onDelete(comment)}
              aria-label={labels.delete}
              title={labels.delete}
              disabled={deleting}
            >
              <TrashIcon />
            </button>
          )}
        </div>
      )}

      <div
        className={cx(
          styles.card,
          isOwn && styles.cardOwn,
          isBeingEdited && styles.cardEditing,
          deleting && styles.cardDeleting
        )}
      >
        <p className={styles.text}>{getCommentText(comment)}</p>

        {attachments.length > 0 && (
          <ul className={styles.attachmentList}>
            {attachments.map((attachment) => (
              <li key={attachment.id} className={styles.attachmentItem}>
                {getAttachmentUrl ? (
                  <IconBlock
                    attachment={attachment}
                    attachments={attachments}
                    getAttachmentUrl={getAttachmentUrl}
                  />
                ) : (
                  <PaperclipIcon />
                )}
                <a
                  href={attachment.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.attachmentLink}
                  title={labels.download}
                  onClick={handleDownloadClick(attachment)}
                >
                  {attachment.name || labels.noName}
                </a>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.metaRow}>
          <span className={styles.metaDate}>{dateLine}</span>
          {authorLine && (
            <span className={styles.metaAuthor}>{authorLine}</span>
          )}
        </div>
      </div>
    </li>
  );
}

const AttachmentShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  fileName: PropTypes.string,
  extension: PropTypes.string,
  url: PropTypes.string,
  size: PropTypes.number,
});

const WorkerShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  login: PropTypes.string,
  surName: PropTypes.string,
  firstName: PropTypes.string,
  patrName: PropTypes.string,
  speciality: PropTypes.string,
  specialityName: PropTypes.string,
  phone: PropTypes.string,
  companyId: PropTypes.number,
  companyName: PropTypes.string,
  fullName: PropTypes.string,
});

export const CommentShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  comment: PropTypes.string,
  text: PropTypes.string,
  addDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  createdAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  updatedAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  worker: WorkerShape,
  authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  author: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fullName: PropTypes.string,
    name: PropTypes.string,
    role: PropTypes.string,
    company: PropTypes.string,
  }),
  attachments: PropTypes.arrayOf(AttachmentShape),
});

CommentItem.propTypes = {
  comment: CommentShape.isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labels: PropTypes.object.isRequired,
  formatAuthor: PropTypes.func,
  formatDate: PropTypes.func,
  isCommentEditable: PropTypes.func,
  readOnly: PropTypes.bool,
  isBeingEdited: PropTypes.bool,
  onDownloadAttachment: PropTypes.func,
  getAttachmentUrl: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  deleting: PropTypes.bool,
};

CommentItem.defaultProps = {
  readOnly: false,
  isBeingEdited: false,
  deleting: false,
};
